import React, { useState, useEffect, useRef } from 'react';
import { Select, Tabs, Spin } from 'antd';
import { 
  FireOutlined, 
  TrophyOutlined, 
  InfoCircleOutlined,
} from '@ant-design/icons';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/global-background.css';
import '../styles/GalleryPage.css';

// 引入卡片特效样式
import '../styles/cards/index.css';
import '../styles/special-cards.css';

// 定义通用的 NFT 数据类型
interface NFTCharacter {
  id: string;
  name: string;
  image: string;
  rarity: string;
  group: string;
  hp: number;
  description: string;
  skills: string[];
}

interface NFTItem {
  id: string;
  name: string;
  image: string;
  rarity: string;
  description: string;
  boost: string;
  usage: string;
}

interface GameNFTData {
  characters: NFTCharacter[];
  items: NFTItem[];
}

// 自定义Modal接口
interface CustomModalProps {
  visible: boolean;
  onCancel: () => void;
  children: React.ReactNode;
  width?: number;
}

// 自定义Modal组件
const CustomModal: React.FC<CustomModalProps> = ({
  visible,
  onCancel,
  children,
  width = 900,
}) => {
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleEscKey);
      // 防止背景滚动
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [visible, onCancel]);

  if (!visible) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div 
      className="custom-modal-overlay"
      onClick={handleBackdropClick}
    >
      <div 
        className="custom-modal-container"
        style={{
          width: `${width}px`,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="custom-modal-header">
          <h3 className="custom-modal-title">NFT 详情</h3>
          <button 
            className="custom-modal-close"
            onClick={onCancel}
          >
            ×
          </button>
        </div>
        <div className="custom-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

const GalleryPage: React.FC = () => {
  const [language, setLanguage] = useState<'en' | 'zh'>('zh');
  const [selectedGame, setSelectedGame] = useState('reloader');
  const [nftData, setNftData] = useState<GameNFTData>({ characters: [], items: [] });
  const [selectedNFT, setSelectedNFT] = useState<any>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 特别鸣谢卡片数据
  const [specialThanks, setSpecialThanks] = useState<NFTCharacter[]>([]);
  
  // 特别鸣谢卡片的状态管理
  const [specialCardState, setSpecialCardState] = useState<{[key: string]: {
    scaled: boolean;
    firstClick: boolean;
    dragging: boolean;
    hasAnimated?: boolean;
  }}>({});
  
  // 添加拖动状态
  const [dragState, setDragState] = useState<{[key: string]: {
    isDragging: boolean;
    startX: number;
    startY: number;
    currentRotateX: number;
    currentRotateY: number;
  }}>({});

  // 卡片交互状态（用于内部状态管理）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [interactionState, setInteractionState] = useState<{[key: string]: {
    active: boolean;
    x: number;
    y: number;
    centerX: number;
    centerY: number;
    posX: number;
    posY: number;
    cardOpacity: number;
    scale: number;
  }}>({});

  // 卡片元素引用
  const cardRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  const [cardRotation, setCardRotation] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const rotationStart = useRef({ x: 0, y: 0 });

  // 图鉴库与json文件映射
  const [galleryOptions, setGalleryOptions] = useState([]);
  const [galleryJsonMap, setGalleryJsonMap] = useState<{ [key: string]: string }>({});

  // 数学辅助函数
  const round = (value: number, precision: number = 3) => parseFloat(value.toFixed(precision));
  
  const clamp = (value: number, min: number = 0, max: number = 100) => {
    return Math.min(Math.max(value, min), max);
  };
  
  const adjust = (value: number, fromMin: number, fromMax: number, toMin: number, toMax: number) => {
    return round(toMin + (toMax - toMin) * (value - fromMin) / (fromMax - fromMin));
  };

  // 测试动画的函数
  const testAnimation = () => {
    const rotators = document.querySelectorAll('.special-card__rotator');
    console.log('Found rotators:', rotators.length);
    rotators.forEach((rotator, index) => {
      rotator.classList.add('first-click-animation');
      setTimeout(() => {
        rotator.classList.remove('first-click-animation');
      }, 1500);
    });
  };

  // 添加到window对象以便在控制台测试
  useEffect(() => {
    (window as any).testAnimation = testAnimation;
  }, []);

  useEffect(() => {
    fetch('/gameLibrary/index.json')
      .then(res => res.json())
      .then(list => {
        setGalleryOptions(list.map((item: any) => ({
          value: item.id,
          label: item.label,
          desc: item.desc,
          cover: item.cover
        })));
        const map: { [key: string]: string } = {};
        list.forEach((item: any) => { map[item.id] = item.file; });
        setGalleryJsonMap(map);
        // 默认选中第一个
        if (list.length > 0) setSelectedGame(list[0].id);
      });
  }, []);

  useEffect(() => {
    if (!galleryJsonMap[selectedGame]) return;
    setLoading(true);
    fetch(galleryJsonMap[selectedGame])
      .then(res => res.json())
      .then(data => {
        // 兼容不同json结构
        let characters = [];
        let items = [];
        if (data.characters) {
          characters = data.characters.map((character: any) => ({
            id: character.id,
            name: character.name,
            image: process.env.PUBLIC_URL + character.image,
            rarity: character.rarity || 'common',
            group: character.group || '',
            hp: character.hp || 0,
            description: character.intro || character.description || '',
            skills: character.skills || [],
          }));
          
          // 提取特别鸣谢卡片（赵四和何一），并设置为最高品级
          const specialIds = ['simayi', 'zhenji']; // simayi是赵四，zhenji是何一
          const specialCharacters = characters
            .filter((char: NFTCharacter) => specialIds.includes(char.id))
            .map((char: NFTCharacter) => ({
              ...char,
              rarity: 'legendary' // 设置为最高品级
            }));
          setSpecialThanks(specialCharacters);
          
          // 从角色列表中过滤掉特别鸣谢的卡片
          characters = characters.filter((char: NFTCharacter) => !specialIds.includes(char.id));
        }
        if (data.items) {
          items = data.items.map((item: any) => ({
            id: item.id,
            name: item.name,
            image: process.env.PUBLIC_URL + item.image,
            rarity: item.rarity || 'common',
            description: item.description || '',
            boost: item.boost || '',
            usage: item.usage || '',
          }));
        }
        setNftData({ characters, items });
        setLoading(false);
      })
      .catch(() => {
        setNftData({ characters: [], items: [] });
        setSpecialThanks([]);
        setLoading(false);
      });
  }, [selectedGame, galleryJsonMap]);

  const handleGameChange = (value: string) => {
    setSelectedGame(value);
  };

  const showNFTDetail = (nft: any) => {
    setSelectedNFT(nft);
    setDetailModalVisible(true);
  };

  // 卡片互动处理函数
  const handleCardInteraction = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>, id: string) => {
    const card = cardRefs.current[id];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    
    // 获取鼠标绝对位置
    const absolute = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    
    // 转换为百分比 (0-100)
    const percent = {
      x: clamp(round((100 / rect.width) * absolute.x)),
      y: clamp(round((100 / rect.height) * absolute.y)),
    };
    
    // 计算从中心的偏移 (-50 到 50)
    const center = {
      x: percent.x - 50,
      y: percent.y - 50,
    };
    
    // 计算从中心的距离
    const fromCenter = clamp(
      Math.sqrt(center.y * center.y + center.x * center.x) / 50,
      0,
      1
    );
    
    // 设置背景位置 (限制在37-63范围内)
    const background = {
      x: adjust(percent.x, 0, 100, 37, 63),
      y: adjust(percent.y, 0, 100, 33, 67),
    };
    
    // 设置旋转角度
    const rotate = {
      x: round(-(center.x / 3.5)),
      y: round(center.y / 2),
    };
    
    // 设置闪光效果
    const glare = {
      x: round(percent.x),
      y: round(percent.y),
      o: 1,
    };
    
    setInteractionState(prev => ({
      ...prev,
      [id]: {
        active: true,
        x: e.clientX,
        y: e.clientY,
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        posX: percent.x / 100,
        posY: percent.y / 100,
        cardOpacity: 1,
        scale: 1,
      }
    }));

    // 添加互动类并设置CSS变量
    card.classList.add('active', 'interacting');
    card.style.setProperty('--pointer-x', `${glare.x}%`);
    card.style.setProperty('--pointer-y', `${glare.y}%`);
    card.style.setProperty('--pointer-from-center', fromCenter.toString());
    card.style.setProperty('--pointer-from-top', (percent.y / 100).toString());
    card.style.setProperty('--pointer-from-left', (percent.x / 100).toString());
    card.style.setProperty('--card-opacity', glare.o.toString());
    card.style.setProperty('--rotate-x', `${rotate.x}deg`);
    card.style.setProperty('--rotate-y', `${rotate.y}deg`);
    card.style.setProperty('--background-x', `${background.x}%`);
    card.style.setProperty('--background-y', `${background.y}%`);
    card.style.setProperty('--card-scale', '1');
  };

  const handleCardLeave = (id: string) => {
    const card = cardRefs.current[id];
    if (!card) return;

    setInteractionState(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        active: false,
        cardOpacity: 0,
        scale: 1,
      }
    }));

    // 移除互动类
    card.classList.remove('active', 'interacting');
    
    // 使用setTimeout实现平滑过渡
    setTimeout(() => {
      card.style.setProperty('--pointer-x', '50%');
      card.style.setProperty('--pointer-y', '50%');
      card.style.setProperty('--pointer-from-center', '0');
      card.style.setProperty('--pointer-from-top', '0.5');
      card.style.setProperty('--pointer-from-left', '0.5');
      card.style.setProperty('--card-opacity', '0');
    card.style.setProperty('--rotate-x', '0deg');
    card.style.setProperty('--rotate-y', '0deg');
      card.style.setProperty('--background-x', '50%');
      card.style.setProperty('--background-y', '50%');
      card.style.setProperty('--card-scale', '1');
    }, 100);
  };

  // 特别鸣谢卡片鼠标交互处理
  const handleSpecialCardMouseMove = (e: React.MouseEvent<HTMLDivElement>, cardId: string) => {
    const card = cardRefs.current[cardId];
    if (!card) return;
    
    const state = specialCardState[cardId];
    if (state?.dragging) {
      // 拖动逻辑在另外的函数处理
      return;
    }

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // 计算百分比位置 (0-100)
    const posX = clamp((x / rect.width) * 100);
    const posY = clamp((y / rect.height) * 100);
    
    // 计算从中心的偏移
    const centerX = posX - 50;
    const centerY = posY - 50;
    
    // 计算从中心的距离 (0-1)
    const hyp = Math.sqrt(centerX * centerX + centerY * centerY) / 50;
    
    // 设置CSS变量
    card.style.setProperty('--mx', `${posX}%`);
    card.style.setProperty('--my', `${posY}%`);
    card.style.setProperty('--posx', `${posX}%`);
    card.style.setProperty('--posy', `${posY}%`);
    card.style.setProperty('--hyp', hyp.toString());
    card.style.setProperty('--o', '1');
    
    // 设置旋转角度
    if (!state?.scaled) {
      // 只在未放大时应用旋转
      card.style.setProperty('--rx', `${centerY / 2}deg`);
      card.style.setProperty('--ry', `${-centerX / 3.5}deg`);
    }
    
    // 添加active和interacting类
    card.classList.add('active', 'interacting');
  };

  const handleSpecialCardMouseLeave = (cardId: string) => {
    const card = cardRefs.current[cardId];
    if (!card) return;
    
    const state = specialCardState[cardId];
    if (state?.scaled) return;
    
    // 移除active和interacting类
    card.classList.remove('active', 'interacting');
    
    // 延迟重置CSS变量，实现平滑过渡
    setTimeout(() => {
      if (!card.classList.contains('active')) {
        card.style.setProperty('--mx', '50%');
        card.style.setProperty('--my', '50%');
        card.style.setProperty('--posx', '50%');
        card.style.setProperty('--posy', '50%');
        card.style.setProperty('--hyp', '0');
        card.style.setProperty('--o', '0');
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
      }
    }, 100);
  };

  const handleSpecialCardClick = (cardId: string) => {
    const card = cardRefs.current[cardId];
    if (!card) return;
    
    const state = specialCardState[cardId] || {
      scaled: false,
      firstClick: true,
      dragging: false,
      hasAnimated: false,
    };
    
    if (state.dragging) return;
    
    const isScaling = !state.scaled;
    const rect = card.getBoundingClientRect();
    
    if (isScaling) {
      // 放大前初始化CSS变量
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
      
      // 放大
      const scaleW = (window.innerWidth / rect.width) * 0.9;
      const scaleH = (window.innerHeight / rect.height) * 0.9;
      const scale = Math.min(scaleW, scaleH, 1.75);
      
      const view = document.documentElement;
      const translateX = view.clientWidth / 2 - rect.x - rect.width / 2;
      const translateY = view.clientHeight / 2 - rect.y - rect.height / 2;
      
      card.style.setProperty('--s', scale.toString());
      card.style.setProperty('--tx', `${translateX}px`);
      card.style.setProperty('--ty', `${translateY}px`);
      
      // 如果是第一次点击且还没播放过动画
      if (state.firstClick && !state.hasAnimated) {
        // 添加动画类
        const rotator = card.querySelector('.special-card__rotator');
        
        if (rotator) {
          rotator.classList.add('first-click-animation');
          // 动画结束后移除类
          setTimeout(() => {
            rotator.classList.remove('first-click-animation');
          }, 1500);
        } else {
          console.error('Rotator element not found!');
        }
      }
    } else {
      // 缩小
      card.style.setProperty('--s', '1');
      card.style.setProperty('--tx', '0px');
      card.style.setProperty('--ty', '0px');
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
    }
    
    setSpecialCardState(prev => ({
      ...prev,
      [cardId]: {
        scaled: isScaling,
        firstClick: false, // 点击后就不再是第一次了
        dragging: false,
        hasAnimated: isScaling && state.firstClick && !state.hasAnimated ? true : (prev[cardId]?.hasAnimated || false),
      }
    }));
  };

  // 拖动处理
  const dragStateRef = useRef<{
    cardId: string | null;
    startX: number;
    startY: number;
    startRx: number;
    startRy: number;
  }>({ cardId: null, startX: 0, startY: 0, startRx: 0, startRy: 0 });

  const handleSpecialCardDragStart = (e: React.MouseEvent<HTMLDivElement>, cardId: string) => {
    const state = specialCardState[cardId];
    if (!state?.scaled) return;
    
    e.preventDefault();
    const card = cardRefs.current[cardId];
    if (!card) return;
    
    const currentRx = parseFloat(card.style.getPropertyValue('--rx') || '0');
    const currentRy = parseFloat(card.style.getPropertyValue('--ry') || '0');
    
    dragStateRef.current = {
      cardId,
      startX: e.clientX,
      startY: e.clientY,
      startRx: currentRx,
      startRy: currentRy,
    };
    
    setSpecialCardState(prev => ({
      ...prev,
      [cardId]: {
        ...prev[cardId],
        dragging: true,
      }
    }));
  };

  const handleSpecialCardDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    const { cardId, startX, startY, startRx, startRy } = dragStateRef.current;
    if (!cardId) return;
    
    const card = cardRefs.current[cardId];
    if (!card) return;
    
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;
    
    card.style.setProperty('--rx', `${startRx + deltaY * 0.5}deg`);
    card.style.setProperty('--ry', `${startRy - deltaX * 0.5}deg`);
  };

  const handleSpecialCardDragEnd = () => {
    const { cardId } = dragStateRef.current;
    if (!cardId) return;
    
    dragStateRef.current = { cardId: null, startX: 0, startY: 0, startRx: 0, startRy: 0 };
    
    setSpecialCardState(prev => ({
      ...prev,
      [cardId]: {
        ...prev[cardId],
        dragging: false,
      }
    }));
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return '#7f7f7f';
      case 'uncommon': return '#1ab34a';
      case 'rare': return '#0070dd';
      case 'epic': return '#a335ee';
      case 'legendary': return '#ff8000';
      default: return '#7f7f7f';
    }
  };

  const getRarityText = (rarity: string) => {
    if (language === 'en') {
      return rarity.charAt(0).toUpperCase() + rarity.slice(1);
    }
    
    const rarityMap: Record<string, string> = {
      'common': '普通',
      'uncommon': '优秀',
      'rare': '稀有',
      'epic': '史诗',
      'legendary': '传说'
    };
    
    return rarityMap[rarity] || rarity;
  };

  // 阵营颜色映射
  const getGroupColor = (group: string) => {
    switch (group) {
      case '项目方': return '#ffb300';      // 橙色
      case '布道者': return '#00bcd4';      // 青色
      case '交易者': return '#4caf50';      // 绿色
      case '散户':   return '#e91e63';      // 粉色
      default:       return '#a335ee';      // 默认紫色
    }
  };

  // 渲染角色卡片
  const renderCharacterCard = (character: NFTCharacter) => {
    const rarityClass = character.rarity.toLowerCase();
    const cardId = `char-${character.id}`;
    
    return (
      <div 
        key={character.id}
        className="card-wrapper"
      >
        <div
          className="card-container" 
          data-rarity={rarityClass}
        >
          <div className="card-badge-rarity">{getRarityText(character.rarity)}</div>
          <div
            ref={(el) => { cardRefs.current[cardId] = el; }}
            className="card"
            data-rarity={rarityClass}
            data-subtypes={character.group ? 'stage-1' : 'basic'}
            data-supertype="pokemon"
          >
            <div className="card__translater">
              <button
                className="card__rotator"
                onClick={() => showNFTDetail(character)}
                onMouseMove={(e) => handleCardInteraction(e, cardId)}
                onMouseLeave={() => handleCardLeave(cardId)}
                aria-label={`Expand the NFT Card: ${character.name}`}
                tabIndex={0}
              >
                <img
                  className="card__back"
                  src={process.env.PUBLIC_URL + '/images/deck/card-back.jpg'}
                  alt="The back of an NFT Card"
                  loading="lazy"
                  width="660"
                  height="921"
                />
                <div className="card__front">
                  <img 
                    src={character.image} 
                    alt={`Front design of the ${character.name} NFT Card`}
                    className="card__artwork"
                    loading="lazy"
                    width="660"
                    height="921"
                  />
                  <span className="card__name">{character.name}</span>
                  <div className="card__shine"></div>
                  <div className="card__glare"></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染特别鸣谢卡片（使用新的结构）
  const renderSpecialThanksCard = (character: NFTCharacter) => {
    const cardId = `special-${character.id}`;
    const state = specialCardState[cardId] || {
      scaled: false,
      firstClick: true,
      dragging: false,
      hasAnimated: false,
    };
    
    return (
      <div 
        key={character.id}
        className="card-wrapper special-thanks-card"
      >
        <div
          ref={(el) => { cardRefs.current[cardId] = el; }}
          className={`special-card ${state.scaled ? 'scaled' : ''} ${state.dragging ? 'dragging' : ''}`}
          data-rarity="rare secret"
          onMouseMove={(e) => handleSpecialCardMouseMove(e, cardId)}
          onMouseLeave={() => handleSpecialCardMouseLeave(cardId)}
        >
          <div className="special-card__translater">
            <div
              className="special-card__rotator"
              onMouseDown={(e) => handleSpecialCardDragStart(e, cardId)}
              onMouseMove={handleSpecialCardDrag}
              onMouseUp={handleSpecialCardDragEnd}
              onMouseLeave={handleSpecialCardDragEnd}
              onClick={() => handleSpecialCardClick(cardId)}
            >
              <img
                className="special-card__back"
                src={process.env.PUBLIC_URL + '/images/deck/default.png'}
                alt="Card back"
                width="220"
                height="365"
              />
              <div className="special-card__front">
                <img 
                  src={character.image} 
                  alt={character.name}
                  className="special-card__artwork"
                  width="220"
                  height="365"
                />
                <div className="special-card__shine"></div>
                <div className="special-card__glare"></div>
                <span className="special-card__name">{character.name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染物品卡片
  const renderItemCard = (item: NFTItem) => {
    const rarityClass = item.rarity.toLowerCase();
    const cardId = `item-${item.id}`;
    
    return (
      <div 
        key={item.id}
        className="card-wrapper"
      >
        <div
          className="card-container" 
          data-rarity={rarityClass}
        >
          <div className="card-badge-rarity">{getRarityText(item.rarity)}</div>
          <div
            ref={(el) => { cardRefs.current[cardId] = el; }}
            className="card"
            data-rarity={rarityClass}
            data-subtypes="item"
            data-supertype="trainer"
          >
            <div className="card__translater">
              <button
                className="card__rotator"
                onClick={() => showNFTDetail(item)}
                onMouseMove={(e) => handleCardInteraction(e, cardId)}
                onMouseLeave={() => handleCardLeave(cardId)}
                aria-label={`Expand the NFT Card: ${item.name}`}
                tabIndex={0}
              >
                <img
                  className="card__back"
                  src={process.env.PUBLIC_URL + '/images/deck/card-back.jpg'}
                  alt="The back of an NFT Card"
                  loading="lazy"
                  width="660"
                  height="921"
                />
                <div className="card__front">
                  <img 
                    src={item.image} 
                    alt={`Front design of the ${item.name} NFT Card`}
                    className="card__artwork"
                    loading="lazy"
                    width="660"
                    height="921"
                  />
                  <span className="card__name">{item.name}</span>
                  <div className="card__shine"></div>
                  <div className="card__glare"></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    rotationStart.current = { ...cardRotation };
  };

  const handleDrag = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    
    setCardRotation({
      x: rotationStart.current.x + dy * 0.5,
      y: rotationStart.current.y + dx * 0.5
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // 渲染详情卡片
  const renderDetailCard = () => {
    if (!selectedNFT) return null;
    // 更精确地判断是否为人物卡
    const isCharacter = typeof selectedNFT.hp !== 'undefined' && typeof selectedNFT.group !== 'undefined';
    const rarityClass = selectedNFT.rarity.toLowerCase();
    const cardId = `detail-card`;
    const cardStyle = {
      transform: `
        perspective(1000px)
        rotateX(${cardRotation.x}deg)
        rotateY(${cardRotation.y}deg)
      `,
      transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(.4,1.6,.6,1)',
      cursor: isDragging ? 'grabbing' : 'grab',
    };
    return (
      <div className="detail-card-container">
        <div className="detail-card-left">
          <div 
            className="card-container detail-card" 
            data-rarity={rarityClass}
          >
            <div className="card-badge-rarity">{getRarityText(selectedNFT.rarity)}</div>
            <div 
              ref={(el) => { cardRefs.current[cardId] = el; }}
              className="card"
              style={cardStyle}
              data-rarity={rarityClass}
              data-subtypes={isCharacter ? 'stage-1' : 'item'}
              data-supertype={isCharacter ? 'pokemon' : 'trainer'}
              onMouseDown={handleDragStart}
              onMouseMove={handleDrag}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
            >
              <div className="card__translater">
                <div className="card__rotator">
                  <img
                    className="card__back"
                    src={process.env.PUBLIC_URL + '/images/deck/card-back.jpg'}
                    alt="The back of an NFT Card"
                    loading="lazy"
                    width="660"
                    height="921"
                  />
                  <div className="card__front">
                    <img 
                      src={selectedNFT.image} 
                      alt={`Front design of the ${selectedNFT.name} NFT Card`}
                      className="card__artwork"
                      loading="lazy"
                      width="660"
                      height="921"
                    />
                    <span className="card__name">{selectedNFT.name}</span>
                    <div className="card__shine"></div>
                    <div className="card__glare"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="detail-card-info">
          <h2 className="detail-card-title">{selectedNFT.name}</h2>
          <div className="detail-card-rarity" style={{color: getRarityColor(selectedNFT.rarity)}}>
            {getRarityText(selectedNFT.rarity)}
          </div>
          <p className="detail-card-description">{selectedNFT.description}</p>
          {isCharacter ? (
            <>
              <h3 className="detail-section-title">
                <FireOutlined /> {language === 'en' ? 'Attributes' : '属性'}
              </h3>
              <div className="detail-attributes">
                <div className="attribute-item">
                  <span className="attribute-label">{language === 'en' ? 'Group' : '阵营'}</span>
                  <span
                    className="attribute-value group-value"
                    style={{
                      color: getGroupColor(selectedNFT.group),
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textShadow: '0 0 8px rgba(0,0,0,0.18)'
                    }}
                  >
                    {selectedNFT.group}
                  </span>
                </div>
                <div className="attribute-item">
                  <span className="attribute-label">HP</span>
                  <span className="attribute-value hp-value" style={{fontSize: '1.2rem', fontWeight: 600, display: 'flex', alignItems: 'center'}}>
                    {Array.from({ length: selectedNFT.hp }).map((_, i) => (
                      <span key={i} style={{color: '#ff4d6d', fontSize: '1.3em', marginRight: 2}}>💗</span>
                    ))}
                    <span style={{marginLeft: 6, color: '#fff', fontSize: '1rem'}}>x{selectedNFT.hp}</span>
                  </span>
                </div>
              </div>
              <h3 className="detail-section-title">
                <TrophyOutlined /> {language === 'en' ? 'Skills' : '技能'}
              </h3>
              <div className="detail-skills">
                {selectedNFT.skills.map((skill: string, index: number) => (
                  <div key={index} className="detail-skill-tag">{skill}</div>
                ))}
              </div>
            </>
          ) : (
            <div className="item-properties">
              <div className="item-property">
                <h3 className="property-title">
                  <FireOutlined /> {language === 'en' ? 'Boost Effect' : '增益效果'}
                </h3>
                <p className="property-value">{selectedNFT.boost}</p>
              </div>
              <div className="item-property">
                <h3 className="property-title">
                  <InfoCircleOutlined /> {language === 'en' ? 'Usage' : '使用限制'}
                </h3>
                <p className="property-value">{selectedNFT.usage}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="nova-container">
      <div className="nova-grid"></div>
      
      <Navbar language={language} />
      
      <div className="content">
        <div className="title-section">
          <h1 className="nova-title">{language === 'en' ? "NFT Gallery" : "NFT图鉴"}</h1>
          <p className="nova-subtitle">
            {language === 'en'
              ? "Explore unique characters and items from our games"
              : "探索我们游戏中的独特角色和物品"}
          </p>
        </div>
        
          <div className="game-selector enhanced-selector vertical-selector" style={{paddingBottom: '50px'}}>
            <span className="selector-label gallery-selector-title">
              {language === 'en' ? "Select Gallery:" : "选择图鉴库:"}
            </span>
            <Select
              value={selectedGame}
              onChange={handleGameChange}
              options={galleryOptions}
              className="game-select gallery-select-large"
              placeholder={language === 'en' ? "Select a gallery" : "选择图鉴库"}
              popupClassName="game-select-dropdown"
            />
          </div>
        
        <div className="gallery-container">
          {loading ? (
            <div className="loading-container">
              <Spin size="large" tip={language === 'en' ? "Loading NFTs..." : "加载中..."} />
            </div>
          ) : (
            <Tabs 
              defaultActiveKey="characters" 
              className="gallery-tabs"
              tabBarGutter={30}
              items={[
                {
                  key: "characters",
                  label: <span><span className="tab-icon">👤</span> {language === 'en' ? "Characters" : "角色"}</span>,
                  children: (
                    <div className="card-pack">
                      <div className="card-pack-header">
                        <span className="card-pack-title">{language === 'en' ? 'Character Pack' : '角色卡包'}</span>
                        <span className="card-pack-count">{language === 'en' ? `Total: ${nftData.characters.length}` : `共${nftData.characters.length}张`}</span>
                      </div>
                      <div className="nft-cards-grid">
                        {nftData.characters.length === 0 ? (
                          <div className="empty-hint">{language === 'en' ? 'Coming soon' : '敬请期待'}</div>
                        ) : (
                          nftData.characters.map(character => renderCharacterCard(character))
                        )}
                      </div>
                    </div>
                  )
                },
                {
                  key: "items",
                  label: <span><span className="tab-icon">🔮</span> {language === 'en' ? "Items" : "物品"}</span>,
                  children: (
                    <div className="card-pack">
                      <div className="card-pack-header">
                        <span className="card-pack-title">{language === 'en' ? 'Item Pack' : '物品卡包'}</span>
                        <span className="card-pack-count">{language === 'en' ? `Total: ${nftData.items.length}` : `共${nftData.items.length}张`}</span>
                      </div>
                      <div className="nft-cards-grid">
                        {nftData.items.length === 0 ? (
                          <div className="empty-hint">{language === 'en' ? 'Coming soon' : '敬请期待'}</div>
                        ) : (
                          nftData.items.map(item => renderItemCard(item))
                        )}
                      </div>
                    </div>
                  )
                },
                {
                  key: "special-thanks",
                  label: <span><span className="tab-icon">⭐</span> {language === 'en' ? "Special Thanks" : "特别鸣谢"}</span>,
                  children: (
                    <div className="card-pack special-thanks-pack">
                      <div className="card-pack-header">
                        <span className="card-pack-title special-thanks-title">
                          {language === 'en' ? ' Special Thanks Collection ' : ' 特别鸣谢系列 '}
                        </span>
                        <span className="card-pack-subtitle">
                          {language === 'en' ? 'Exclusive Secret Gold Edition' : 'Exclusive Secret Gold Edition'}
                        </span>
                      </div>
                      <div className="special-thanks-description">
                        {language === 'en' 
                          ? 'Special thanks to Binance Smart Chain for providing technical support for this project.'
                          : '感谢Binance Smart Chain于本项目提供的技术支持。'}
                      </div>
                      <div className="nft-cards-grid special-thanks-grid">
                        {specialThanks.map(character => renderSpecialThanksCard(character))}
                      </div>
                    </div>
                  )
                }
              ]}
            />
          )}
        </div>
        
        <CustomModal
          visible={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          width={900}
        >
          {selectedNFT && renderDetailCard()}
        </CustomModal>
        
        <Footer language={language} setLanguage={setLanguage} />
      </div>
    </div>
  );
};

export default GalleryPage; 